import { Link } from "@tanstack/react-router";
import { Facebook, Instagram, Youtube, ShieldCheck } from "lucide-react";
import { useState, type FormEvent } from "react";
import { toast } from "sonner";
import { Monogram } from "@/components/site/Monogram";

export function Footer() {
  const [email, setEmail] = useState("");

  const onSubscribe = (e: FormEvent) => {
    e.preventDefault();
    if (!email.includes("@")) return;
    toast.success("Welcome to the Maison. Your code is on its way.");
    setEmail("");
  };

  return (
    <footer className="mt-32 border-t border-gold/20 bg-ink">
      <div className="container-luxe py-20 grid gap-14 lg:grid-cols-[1.4fr_1fr_1fr_1fr]">
        {/* Newsletter / brand */}
        <div>
          <div className="flex items-center gap-3">
            <Monogram className="h-10 w-10 text-gold shrink-0" strokeWidth={5} />
            <div className="font-serif text-3xl tracking-[0.32em] text-gradient-gold">KAKAIYS</div>
          </div>
          <h3 className="font-serif text-2xl mt-8 leading-tight">Get 10% off your first jacket.</h3>
          <p className="mt-3 text-sm text-gold/80">
            Join 33,800+ collectors. Code delivered to your inbox instantly.
          </p>
          <form onSubmit={onSubscribe} className="mt-6 flex items-end gap-4 border-b border-gold/30 pb-2">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email address"
              className="flex-1 bg-transparent text-sm text-gold placeholder:text-gold/50 focus:outline-none py-2"
            />
            <button
              type="submit"
              className="text-[11px] uppercase tracking-[0.28em] text-gold hover:text-gold/80 transition-colors whitespace-nowrap pb-1"
            >
              Send my code
            </button>
          </form>

          <div className="mt-8 inline-flex items-center gap-2 border border-gold/20 px-3 py-2">
            <ShieldCheck className="h-4 w-4 text-gold" />
            <div className="text-[10px] uppercase tracking-[0.24em] text-gold/80">
              Maison Verified · <span className="text-gold/80">Certified Atelier</span>
            </div>
          </div>

          <div className="mt-8 flex items-center gap-3">
            <SocialIcon href="https://facebook.com" label="Facebook"><Facebook className="h-4 w-4" /></SocialIcon>
            <SocialIcon href="https://instagram.com" label="Instagram"><Instagram className="h-4 w-4" /></SocialIcon>
            <SocialIcon href="https://youtube.com" label="YouTube"><Youtube className="h-4 w-4" /></SocialIcon>
            <SocialIcon href="https://tiktok.com" label="TikTok">
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                <path d="M19.6 6.3a5.7 5.7 0 0 1-3.5-1.2 5.7 5.7 0 0 1-2.2-4H10.7v13.6a3 3 0 1 1-3-3c.3 0 .6 0 .9.1V8.3a6.5 6.5 0 0 0-.9-.1 6.4 6.4 0 1 0 6.4 6.4V9.4a8.9 8.9 0 0 0 5.5 1.9V8.1a5.7 5.7 0 0 1 0-1.8z" />
              </svg>
            </SocialIcon>
          </div>
        </div>

        {/* Shop */}
        <FooterColumn title="Shop">
          <FooterLink to="/new-arrivals">New Arrivals</FooterLink>
          <FooterLink to="/women">Women</FooterLink>
          <FooterLink to="/men">Men</FooterLink>
          <FooterLink to="/unisex">Unisex</FooterLink>
          <FooterLink to="/accessories">Accessories</FooterLink>
          <FooterLink to="/riders">Riders</FooterLink>
          <FooterLink to="/shop">Best Sellers</FooterLink>
        </FooterColumn>

        {/* Maison */}
        <FooterColumn title="Maison">
          <FooterLink to="/about">Our Story</FooterLink>
          <FooterStatic>The Atelier</FooterStatic>
          <FooterStatic>Leather Care Guide</FooterStatic>
          <FooterStatic>Size Guide</FooterStatic>
          <FooterStatic>Customer Gallery</FooterStatic>
          <FooterStatic>Customer Reviews</FooterStatic>
          <FooterStatic>Buying Guide</FooterStatic>
        </FooterColumn>

        {/* Help */}
        <FooterColumn title="Help">
          <FooterLink to="/contact">Customer Care</FooterLink>
          <FooterLink to="/contact">Contact Us</FooterLink>
          <FooterLink to="/shipping">Shipping Policy</FooterLink>
          <FooterLink to="/returns">Returns &amp; Exchanges</FooterLink>
          <FooterLink to="/terms">Terms of Service</FooterLink>
          <FooterLink to="/privacy">Privacy Policy</FooterLink>
        </FooterColumn>
      </div>

      <div className="border-t border-gold/15">
        <div className="container-luxe py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-gold/80 tracking-[0.2em] uppercase">
          <span>© {new Date().getFullYear()} <span className="text-gold/80">Kakaiys Maison</span> — All rights reserved</span>
          <span>Sialkot · Milano · Paris</span>
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="eyebrow mb-5 text-gold/80">{title}</div>
      <ul className="space-y-3 text-sm">{children}</ul>
    </div>
  );
}

function FooterLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <li>
      <Link to={to} preload="intent" className="underline-grow text-gold/80 hover:text-gold">
        {children}
      </Link>
    </li>
  );
}

function FooterStatic({ children }: { children: React.ReactNode }) {
  return (
    <li>
      <span className="underline-grow cursor-pointer text-gold/80 hover:text-gold">
        {children}
      </span>
    </li>
  );
}

function SocialIcon({ href, label, children }: { href: string; label: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      aria-label={label}
      className="h-9 w-9 inline-flex items-center justify-center border border-gold/30 text-gold/80 hover:text-ink hover:bg-gold transition-colors"
    >
      {children}
    </a>
  );
}
